import React, { useState, useMemo } from 'react';
import '@styles/editField/TimeFrameEdit.css';
import { SelectMenu } from 'SelectMenu';
import { useTranslation } from 'react-i18next';
import DayPickerInput from './DayPickerInput';
import { upper } from '@managers/helpers';
import { ErrorList } from './PlannerModeSelectors';
import $ from 'jquery';
import dayjs from '../../../utils/dayjsConfig';
import { buildActivePeriod } from './timeFrameDateHelpers';

const startHourOptions = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return { title: `${h}:${m}`, value: `${h}:${m}` };
});

let endHoursMenuOptions = [];

const setEndHoursMenuOptions = () => {
  let hours = [];
  for (let j = 0; j < 2; j++) {
    for (let i = 0; i < 48; i++) {
      let n = i % 2 == 0 ? i / 2 + ':00' : (i + 1) / 2 - 1 + ':30';
      if (i < 20) {
        n = '0' + n;
      }
      if (j) {
        if (i === 0) {
          hours.push({ title: '-' + 'Next day' + '-', value: '' });
        }
        hours.push({ title: n + '⁺¹', value: n + '+1' });
      } else {
        hours.push({ title: n, value: n });
      }
    }
  }
  return hours;
};
endHoursMenuOptions = setEndHoursMenuOptions();

// Add LabelInput subcomponent for label/input or label/value display
const LabelInput = ({
  label,
  isMandatory = false,
  children,
  className = '',
  labelClassName = '',
  style = {},
  ...props
}) => (
  <div className={`tfedit-field ${className}`} style={style}>
    <span className={`tfedit-label ${labelClassName}`}>
      {label}
      {isMandatory && <span className='tfedit-required'>*</span>}
    </span>
    {children}
  </div>
);

const hasError = (errors, field) => {
  for (const [key, value] of Object.entries(errors)) {
    if (value.field === field) {
      return true;
    }
  }
  return false;
};

// Reusable date picker field
const DatePickerField = ({
  label,
  value,
  onChange,
  inputId,
  inputProps = {},
  isValidDate,
  clearable = false,
}) => {
  const { t } = useTranslation();
  return (
    <LabelInput label={label} className='tf-cell'>
      <DayPickerInput
        value={value}
        onChange={onChange}
        dayPickerProps={{
          onChangeRaw: (e) => e.preventDefault(),
          // Show react-datepicker's clear button so returning a date to its
          // empty ("no limitation") state is discoverable, not only via
          // Delete/Backspace.
          isClearable: clearable,
        }}
        inputProps={{
          placeholder: upper(t('select')) + '...',
          style: { width: '100%', height: '40px', backgroundColor: 'white' },
          onKeyDown: clearable
            ? (e) => {
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  onChange(null);
                }
              }
            : undefined,
          ...inputProps,
        }}
        dateFormat={'DD MMM YYYY'}
        inputId={inputId}
        isValidDate={isValidDate}
      />
    </LabelInput>
  );
};

// Reusable ValueInputandSource for Min/Max input + source
const ValueInputandSource = ({
  label,
  value,
  onChange,
  extraData,
  inputClassName = '',
  hasError = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  if (!value) {
    inputClassName += ' tfedit-input-placeholder';
  }
  if (hasError) {
    inputClassName += ' tfedit-input-error';
  }
  if (disabled) {
    inputClassName += ' tfedit-input-disabled';
  }
  return (
    <div className='d-flex flex-row tf-wide-cell' style={{ flex: 1 }}>
      <LabelInput
        label={label}
        className='d-flex flex-column flex-shrink'
        style={{ marginRight: '8px' }}
      >
        <input
          className={inputClassName}
          type='number'
          value={value}
          onChange={onChange}
          placeholder={disabled ? '–' : extraData.placeholder}
          min={0}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'e') {
              e.preventDefault();
            }
          }}
          disabled={disabled}
        />
      </LabelInput>
      {!disabled && (
        <div className='tfedit-source-wrap' style={{ marginTop: 4 }}>
          <span className='tfedit-source-label'>{t('source')}:</span>
          <span
            className='tfedit-source-tag'
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            tabIndex={0}
            style={{ position: 'relative' }}
          >
            {extraData.source}
            {extraData.tooltip && showTooltip && (
              <div className='tfedit-tooltip'>
                <div className='tfedit-tooltip-arrow' />
                <div className='tfedit-tooltip-content'>
                  {extraData.tooltip}
                </div>
              </div>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

const getDefaultFrequency = (tf, typeConfig) => {
  const options = typeConfig?.frequencyOptions || [];
  // If tf already has a value, use it
  if (tf && tf[typeConfig?.key] !== undefined) return tf[typeConfig.key];
  // Take default option from typeConfig if exists
  if (typeConfig?.defaultOption) {
    return typeConfig?.defaultOption;
  }
  // fallback
  return options[0]?.value;
};

const TimeFrameEdit = ({
  id,
  tf = {},
  minData = {},
  maxData = {},
  computeSourceData,
  userLengthUnits = '',
  onSave,
  onCancel,
  sowDate,
  endOfSeason,
  firstDayOfWeek,
  typeConfig,
}) => {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState(() =>
    getDefaultFrequency(tf, typeConfig),
  );
  const [startHour, setStartHour] = useState(tf.start_hour || '00:00');
  const [endHour, setEndHour] = useState(tf.end_hour || '00:30');
  const [startDate, setStartDate] = useState(() => {
    if (!tf.active_period_start_date) return null;
    const parsed = dayjs(tf.active_period_start_date);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : null;
  });
  const [endDate, setEndDate] = useState(() => {
    if (!tf.active_period_end_date) return null;
    const parsed = dayjs(tf.active_period_end_date);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : null;
  });
  const [min, setMin] = useState(tf.min);
  const [max, setMax] = useState(tf.max);
  const [errors, setErrors] = useState({});
  const [isDoneDisabled, setIsDoneDisabled] = useState(false);

  // Date validation helpers
  const isAfterSowDate = (date) => {
    return sowDate
      ? dayjs(date, 'DD MMM YYYY').isSameOrAfter(dayjs(sowDate), 'day')
      : true;
  };

  const cyclicWeekdayIndex = (dateIndex, diff) => {
    return (dateIndex - diff + 7) % 7;
  };

  const isStartDateValid = (date) => {
    const firstDayNum = typeConfig.shouldBlockPicker
      ? cyclicWeekdayIndex(date.isoWeekday(), 1) == firstDayOfWeek
      : true;
    const isSowDate = sowDate
      ? dayjs(date, 'DD MMM YYYY').isSame(dayjs(sowDate), 'day')
      : true;
    return isAfterSowDate(date) && (firstDayNum || isSowDate);
  };

  const isEndDateValid = (date, startDate) => {
    const endDayNum = typeConfig.shouldBlockPicker
      ? cyclicWeekdayIndex(date.isoWeekday(), 1) ==
        cyclicWeekdayIndex(firstDayOfWeek, 1)
      : true;
    const isEndOfSeason = endOfSeason
      ? dayjs(date, 'DD MMM YYYY').isSame(dayjs(endOfSeason), 'day')
      : true;
    const isAfterStartDate = startDate
      ? dayjs(date, 'DD MMM YYYY').isSameOrAfter(dayjs(startDate), 'day')
      : true;
    return (
      isAfterSowDate(date) && (endDayNum || isEndOfSeason) && isAfterStartDate
    );
  };

  // Helper to update field and re-validate
  const updateField = (field, value) => {
    switch (field) {
      case typeConfig?.key:
        setFrequency(value);
        break;
      case 'startHour':
        setStartHour(value);
        break;
      case 'endHour':
        setEndHour(value);
        break;
      case 'startDate':
        setStartDate(value);
        break;
      case 'endDate':
        setEndDate(value);
        break;
      case 'min':
        setMin(value);
        break;
      case 'max':
        setMax(value);
        break;
      default:
        break;
    }

    setIsDoneDisabled(false);
  };

  const handleSave = () => {
    // Normalize data
    const normalized = {
      id: tf.id || id,
    };
    normalized[typeConfig?.key] =
      frequency || typeConfig?.frequencyOptions?.[0]?.value;
    if (typeConfig?.showHours) {
      normalized.start_hour = startHour || '00:00';
      normalized.end_hour = endHour || '00:30';
    }
    normalized.min = parseFloat(min) || parseFloat(minData.placeholder);
    normalized.max = parseFloat(max) || parseFloat(maxData.placeholder);

    // An empty end date means "until further notice" - the constraint has no
    // upper bound and the key is omitted. See timeFrameDateHelpers.
    Object.assign(normalized, buildActivePeriod(startDate, endDate, sowDate));

    let errs = onSave(normalized);
    if (errs && Object.keys(errs).length > 0) {
      setErrors(errs);
      setIsDoneDisabled(true);
    }
  };

  // Use modeConfig for label and options
  const frequencyLabel = typeConfig?.label;
  const frequencyOptions = typeConfig?.frequencyOptions;

  const isMinMaxDisabled = frequency == 0;

  // Recompute the min/max source live from the current input, so the source
  // label (e.g. "Defined by user") updates while editing, not only after Done.
  const { minData: liveMinData, maxData: liveMaxData } = useMemo(() => {
    if (!computeSourceData) {
      return { minData, maxData };
    }
    const draftTimeFrame = { ...tf };
    draftTimeFrame[typeConfig?.key] = frequency;
    if (typeConfig?.showHours) {
      draftTimeFrame.start_hour = startHour;
      draftTimeFrame.end_hour = endHour;
    }
    if (min !== '' && min != null) {
      draftTimeFrame.min = min;
    } else {
      delete draftTimeFrame.min;
    }
    if (max !== '' && max != null) {
      draftTimeFrame.max = max;
    } else {
      delete draftTimeFrame.max;
    }
    return computeSourceData(draftTimeFrame);
  }, [
    computeSourceData,
    tf,
    typeConfig?.key,
    typeConfig?.showHours,
    frequency,
    startHour,
    endHour,
    min,
    max,
    minData,
    maxData,
  ]);

  return (
    <div className='tfedit-card'>
      <div className='tfedit-content'>
        <div className='tfedit-row tfedit-row-1'>
          <LabelInput
            label={upper(frequencyLabel)}
            isMandatory={true}
            className='tf-cell'
          >
            <SelectMenu
              options={frequencyOptions}
              curValue={frequency}
              onChange={(val) => updateField(typeConfig?.key, val)}
              menuStyle={{
                height: '40px',
                border: '1px solid var(--color-grey-91)',
              }}
              selectClassName={'tfedit-select-menu'}
              sortBox={'sortBox'}
            />
          </LabelInput>
          {typeConfig?.showHours && (
            <div className='d-flex flex-row tf-cell'>
              <LabelInput
                label={t('start_hour')}
                className='tfedit-field-start-hour tf-cell'
              >
                <SelectMenu
                  options={startHourOptions}
                  curValue={startHour}
                  onChange={(val) => updateField('startHour', val)}
                  menuStyle={{ height: '40px' }}
                  selectClassName={
                    hasError(errors, 'startHour')
                      ? 'tfedit-select-menu tfedit-select-menu-error'
                      : 'tfedit-select-menu'
                  }
                  sortBox={'sortBox'}
                />
              </LabelInput>
              <LabelInput label={t('end_hour')} className='tf-cell'>
                <SelectMenu
                  options={endHoursMenuOptions}
                  curValue={endHour}
                  onChange={(val) => updateField('endHour', val)}
                  menuStyle={{ height: '40px' }}
                  selectClassName={'tfedit-select-menu'}
                  sortBox={'sortBox'}
                />
              </LabelInput>
            </div>
          )}
          <DatePickerField
            label={t('start_date')}
            value={startDate}
            onChange={(val) => updateField('startDate', val)}
            inputProps={{
              id: 'start_date_input',
              placeholder: sowDate
                ? dayjs(sowDate).format('DD MMM YYYY')
                : upper(t('select')) + '...',
            }}
            inputId='start_date_input'
            isValidDate={isStartDateValid}
          />
          <DatePickerField
            label={t('end_date')}
            value={endDate}
            inputProps={{
              id: 'end_date_input',
              placeholder: t('Timeframe_no_end_date'),
            }}
            onChange={(val) => updateField('endDate', val)}
            inputId='end_date_input'
            isValidDate={(date) => isEndDateValid(date, startDate)}
            clearable={true}
          />
        </div>
        <div className='tfedit-row tfedit-row-2'>
          {/* Min value */}
          <ValueInputandSource
            label={`${upper(t('min'))} (${userLengthUnits})`}
            value={min}
            onChange={(e) => updateField('min', e.target.value)}
            inputClassName='tfedit-input -webkit-inner-spin-button'
            extraData={liveMinData}
            hasError={hasError(errors, 'min')}
            disabled={isMinMaxDisabled}
          />
          {/* Max value */}
          <ValueInputandSource
            label={`${upper(t('max'))} (${userLengthUnits})`}
            value={max}
            onChange={(e) => updateField('max', e.target.value)}
            inputClassName={`tfedit-input${errors.max ? ' tfedit-input-error' : ''}`}
            extraData={liveMaxData}
            style={{ flex: 1 }}
            hasError={hasError(errors, 'max')}
            disabled={isMinMaxDisabled}
          />
          <div style={{ flex: 0.65 }} />
        </div>
        <div className='d-flex justify-content-end flex-column'>
          <ErrorList errors={errors} />
        </div>
        <div className='tfedit-footer'>
          <button
            className='tfedit-btn tfedit-btn-cancel mr-2'
            onClick={onCancel}
          >
            {t('cancel')}
          </button>
          <button
            type='button'
            className='tfedit-btn tfedit-btn-done'
            disabled={isDoneDisabled}
            onClick={handleSave}
          >
            {t('done_button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimeFrameEdit;
